/**
 * Viewpoint planning module.
 * Resolves AI-authored viewpoint requests into the same planner path WebSpark uses:
 * resolve bounds, collect target IDs, select a camera, then aim at bounds or the
 * planner's visible-surface aim point.
 */
import type {
  ViewpointBounds,
  ViewpointPreset,
  ViewpointSceneContext,
  ViewpointToolData,
  ViewpointVector3,
} from '../generate-jig.types'
import type { ViewCandidate, ViewDecisionResult } from './view-decision-types'
import type { VisibilityMetrics } from './visibility-scorer'
import { isInternalJigNodeReference } from '../jig-internal-nodes'
import { getViewpointFamily, selectView } from './view-search-planner'
import { buildTargetPosition, computeAdaptiveMinDistance, sub, dot } from './viewpoint-math'
import { isViewpointPreset, VIEWPOINT_PRESETS } from './viewpoint-presets'

/**
 * Fully planned camera result returned by the viewpoint planner.
 * This extends the high-level decision metadata with the concrete transform that
 * can be written into a Jig stage.
 */
export interface PlannedViewpoint extends ViewDecisionResult {
  cameraPosition?: ViewpointVector3
  targetPosition?: ViewpointVector3
  preciseMetrics?: null
}

function getResolvedPreset(tool: ViewpointToolData): ViewpointPreset {
  const preset = tool && tool.preset ? tool.preset : 'front'
  return isViewpointPreset(preset) ? preset : 'front'
}

function getValidDistance(value: any): number | undefined {
  if (typeof value !== 'number' || !isFinite(value) || value <= 0) {
    return undefined
  }

  return value
}

function getLegacyTargetY(value: any): number {
  return typeof value === 'number' && isFinite(value) ? value : 0
}

function resolveBoundsByName(sceneContext: ViewpointSceneContext, elementName: string): ViewpointBounds | null {
  const searchName = elementName.trim().toLowerCase()

  if (!searchName) {
    return null
  }

  let exactMatch: ViewpointBounds | null = null
  sceneContext.elementBounds.forEach(function (bounds: ViewpointBounds, alias: string) {
    const loweredAlias = alias.toLowerCase()
    if (isInternalJigNodeReference(loweredAlias)) {
      return
    }

    if (loweredAlias === searchName) {
      exactMatch = bounds
    }
  })

  if (exactMatch) {
    return exactMatch
  }

  let partialMatch: ViewpointBounds | null = null
  sceneContext.elementBounds.forEach(function (bounds: ViewpointBounds, alias: string) {
    const loweredAlias = alias.toLowerCase()
    if (isInternalJigNodeReference(loweredAlias)) {
      return
    }

    if (!partialMatch && (loweredAlias.indexOf(searchName) >= 0 || searchName.indexOf(loweredAlias) >= 0)) {
      partialMatch = bounds
    }
  })

  return partialMatch
}

function distanceBetween(a: ViewpointVector3, b: ViewpointVector3): number {
  const delta = sub(a, b)
  return Math.sqrt(dot(delta, delta))
}

function resolveTargetPosition(
  targetBounds: ViewpointBounds,
  targetY: any,
  plannerMetrics?: VisibilityMetrics
): ViewpointVector3 {
  if (
    !(typeof targetY === 'number' && isFinite(targetY)) &&
    plannerMetrics &&
    plannerMetrics.quality === 'degraded' &&
    plannerMetrics.visibleSampleRatio > 0 &&
    plannerMetrics.visibleSampleRatio < 1
  ) {
    return {
      x: plannerMetrics.aimPoint.x,
      y: plannerMetrics.aimPoint.y,
      z: plannerMetrics.aimPoint.z,
    }
  }

  return buildTargetPosition(targetBounds, typeof targetY === 'number' && isFinite(targetY) ? targetY : 0.5)
}

function buildTargetElementIds(sceneContext: ViewpointSceneContext, elementName?: string, focusNames?: string[]): Set<string> {
  const ids = new Set<string>()

  function addByName(name?: string): void {
    if (!name) {
      return
    }

    const loweredName = name.toLowerCase()
    sceneContext.elements.forEach(function (element) {
      if (element.id.toLowerCase() === loweredName) {
        ids.add(element.id)
        return
      }

      if (element.aliases.some(function (alias) { return alias.toLowerCase() === loweredName })) {
        ids.add(element.id)
      }
    })
  }

  addByName(elementName)
  if (Array.isArray(focusNames)) {
    focusNames.forEach(addByName)
  }

  return ids
}

function toDirectionLabel(candidate: ViewCandidate): string {
  return candidate.sourceSeed === 'requested'
    ? candidate.sourcePreset
    : candidate.sourceSeed + '-' + candidate.sourcePreset
}

function toPlannedViewpoint(
  tool: ViewpointToolData,
  preset: ViewpointPreset,
  reasonCode: PlannedViewpoint['reasonCode'],
  candidate: ViewCandidate,
  metrics: VisibilityMetrics,
  targetPosition: ViewpointVector3,
  note: string,
  usedFallback: boolean
): PlannedViewpoint {
  const distance = distanceBetween(candidate.cameraPosition, targetPosition)

  return {
    capabilityMode: 'degraded-best-effort',
    reasonCode: reasonCode,
    requestedPreset: preset,
    selectedPreset: candidate.sourcePreset,
    selectedFamily: getViewpointFamily(candidate.sourcePreset),
    directionLabel: toDirectionLabel(candidate),
    targetName: tool.elementName ? String(tool.elementName) : undefined,
    targetPosition: targetPosition,
    cameraPosition: candidate.cameraPosition,
    distance: distance,
    degraded: usedFallback || metrics.quality === 'degraded',
    usedFallback: usedFallback,
    note: note,
    preciseMetrics: null,
  }
}

function createLegacyPlannedViewpoint(
  tool: ViewpointToolData,
  preset: ViewpointPreset,
  reasonCode: PlannedViewpoint['reasonCode'],
  note: string
): PlannedViewpoint {
  const config = VIEWPOINT_PRESETS[preset]
  const distance = getValidDistance(tool.distance) || config.distance
  const distanceRatio = distance / config.distance
  const cameraPosition = {
    x: config.position.x * distanceRatio,
    y: config.position.y * distanceRatio,
    z: config.position.z * distanceRatio,
  }
  const targetPosition = { x: 0, y: getLegacyTargetY(tool.targetY), z: 0 }

  return {
    capabilityMode: 'degraded-best-effort',
    reasonCode: reasonCode,
    requestedPreset: preset,
    selectedPreset: preset,
    selectedFamily: getViewpointFamily(preset),
    directionLabel: preset,
    targetName: tool.elementName ? String(tool.elementName) : undefined,
    targetPosition: targetPosition,
    cameraPosition: cameraPosition,
    distance: distanceBetween(cameraPosition, targetPosition),
    degraded: true,
    usedFallback: true,
    note: note,
    preciseMetrics: null,
  }
}

function planWithScene(
  tool: ViewpointToolData,
  preset: ViewpointPreset,
  sceneContext: ViewpointSceneContext,
  requestedTargetBounds: ViewpointBounds | null,
  reasonCode: PlannedViewpoint['reasonCode'],
  note: string,
  usedFallback: boolean
): PlannedViewpoint {
  const targetBounds = requestedTargetBounds || sceneContext.sceneBounds
  const plannerResult = selectView({
    sceneContext: sceneContext,
    targetBounds: targetBounds,
    requestedPreset: preset,
    explicitDistance: getValidDistance(tool.distance),
    minDistance: computeAdaptiveMinDistance(targetBounds, sceneContext.globalScale),
    targetElementIds: buildTargetElementIds(sceneContext, tool.elementName ? String(tool.elementName) : undefined, tool._focusObjectNames),
  })
  const targetPosition = resolveTargetPosition(targetBounds, tool.targetY, plannerResult.metrics)
  const selectedFallback = usedFallback || plannerResult.candidate.sourceSeed !== 'requested'

  return toPlannedViewpoint(
    tool,
    preset,
    selectedFallback ? 'fallback_used' : reasonCode,
    plannerResult.candidate,
    plannerResult.metrics,
    targetPosition,
    note,
    selectedFallback
  )
}

function planSceneOverview(tool: ViewpointToolData, preset: ViewpointPreset, sceneContext: ViewpointSceneContext | null): PlannedViewpoint {
  if (sceneContext) {
    return planWithScene(tool, preset, sceneContext, sceneContext.sceneBounds, 'requested_kept', 'scene overview path', false)
  }

  return createLegacyPlannedViewpoint(tool, preset, 'missing_scene_context', 'scene overview legacy path')
}

/**
 * Resolves a requested viewpoint into a concrete camera, using scene or legacy
 * framing fallbacks instead of silently skipping a stage camera.
 */
export function planViewpoint(tool: ViewpointToolData, sceneContext: ViewpointSceneContext | null): PlannedViewpoint | null {
  if (!tool) {
    return null
  }

  const preset = getResolvedPreset(tool)

  if (!tool.elementName) {
    return planSceneOverview(tool, preset, sceneContext)
  }

  if (!sceneContext) {
    return createLegacyPlannedViewpoint(tool, preset, 'missing_scene_context', 'targeted viewpoint legacy path')
  }

  const resolvedTargetBounds = resolveBoundsByName(sceneContext, String(tool.elementName))

  if (!resolvedTargetBounds) {
    return planWithScene(tool, preset, sceneContext, null, 'fallback_used', 'unresolved_target_scene_bounds', true)
  }

  return planWithScene(tool, preset, sceneContext, resolvedTargetBounds, 'requested_kept', 'requested_target_path', false)
}
